<template>
  <Filters
    :available-markets="availableMarkets?.markets"
    :is-all-tab
    :is-pending-tab
    @filtering="handleFiltering"
  />

  <RevCard class="mx-0 my-28 overflow-hidden">
    <NoResults v-if="showContentBanner" :has-filters />

    <OrdersTable
      v-if="showBuybackOrders"
      :count
      :filters
      :has-next-page="Boolean(nextPageNumber)"
      :is-all-tab
      :is-loading-more="isLoadingMoreOrders"
      :is-pending-tab
      :is-received-tab
      :orders="buybackOrders"
      @load-next-page="fetchNextOrders"
    />

    <RevLoadingScreen
      v-if="isLoadingOrders"
      class="py-44"
      :text="i18n(translations.loadingText)"
      variant="light"
    />
  </RevCard>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import { computed, ref } from 'vue'

import type { HttpApiError } from '@backmarket/http-api'
import {
  getMerchant,
  getMerchantOrderPendingReply,
  getMerchantOrders,
  getMerchantOrdersReceived,
} from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher'
import type { MerchantOrder } from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher.types'
import toastTranslations from '@backmarket/nuxt-layer-buyback/utils/Toast.translations'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevCard } from '@ds/components/Card'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'

import { ROUTE_NAMES } from '~/constants/routes'

import type OrderFilters from '../Filters/Filters.types'
import Filters from '../Filters/Filters.vue'
import NoResults from '../NoResults/NoResults.vue'
import OrdersTable from '../OrdersTable/OrdersTable.vue'

import translations from './Tab.translations'

const i18n = useI18n()
const logger = useLogger()
const route = useRoute()
const { openErrorToast } = useTheToast()
const isLoadingOrders = ref(false)
const filters = ref<OrderFilters>({
  countryCode: '',
  customer: '',
  creationDateMax: '',
  creationDateMin: '',
  sourcingOrderId: '',
  state: '',
  suspendReason: '',
  trackingNumber: '',
})
const buybackOrders = ref<Array<MerchantOrder>>([])
const count = ref(0)
const next = ref<string | null>(null)
const isLoadingMoreOrders = ref(false)

const isAllTab = computed(() => route.name === ROUTE_NAMES.BUYBACK.ORDERS.ALL)
const isReceivedTab = computed(
  () => route.name === ROUTE_NAMES.BUYBACK.ORDERS.ACTIVE,
)
const isPendingTab = computed(
  () => route.name === ROUTE_NAMES.BUYBACK.ORDERS.PENDING,
)
const tabName = computed(() => {
  const tabs: Record<string, boolean> = {
    all: isAllTab.value,
    pending: isPendingTab.value,
    received: isReceivedTab.value,
  }

  return Object.keys(tabs).find((key) => tabs[key] === true)
})
const nextPageNumber = computed(() => {
  if (next.value) {
    return new URL(next.value).searchParams.get('page')
  }

  return null
})
const hasFilters = computed(() => !isEmpty(filters.value))
const hasOrders = computed(() => !isEmpty(buybackOrders.value))
const showBuybackOrders = computed(
  () => hasOrders.value && !isLoadingOrders.value,
)
const showContentBanner = computed(
  () => !hasOrders.value && !isLoadingOrders.value,
)
const requestUrl = computed(() => {
  if (isReceivedTab.value) {
    return getMerchantOrdersReceived
  }
  if (isPendingTab.value) {
    return getMerchantOrderPendingReply
  }

  return getMerchantOrders
})

const { data: availableMarkets, error: getMerchantError } =
  await useHttpFetch(getMerchant)

if (getMerchantError.value) {
  openErrorToast({
    title: i18n(toastTranslations.toastErrorTitle),
    content: i18n(toastTranslations.toastErrorContent),
  })

  logger.error('[BUYBACK][API][ORDERS] Error getting refurbisher', {
    error: getMerchantError.value,
    owners: ['bot-squad-circularity-order-lifecycle-front'],
  })
}

const { data: orders, error: ordersError } = await useHttpFetch(
  requestUrl.value,
)

if (ordersError.value) {
  openErrorToast({
    title: i18n(toastTranslations.toastErrorTitle),
    content: i18n(toastTranslations.toastErrorContent),
  })

  logger.error(`[BUYBACK][API][ORDERS] Error getting ${tabName.value} orders`, {
    error: ordersError.value,
    owners: ['bot-squad-circularity-order-lifecycle-front'],
  })
}

if (orders.value) {
  buybackOrders.value = orders.value.results
  count.value = orders.value.count
  next.value = orders.value.next
}

async function fetchOrders(loadNextPage = false) {
  try {
    isLoadingOrders.value = !loadNextPage
    const response = await $httpFetch(requestUrl.value, {
      queryParams: {
        ...filters.value,
        ...(loadNextPage && {
          page: nextPageNumber.value,
        }),
      },
    })

    if (!isEmpty(response)) {
      buybackOrders.value = loadNextPage
        ? [...buybackOrders.value, ...response.results]
        : response.results
      count.value = response.count
      next.value = response.next
    }
  } catch (err) {
    openErrorToast({
      title: i18n(toastTranslations.toastErrorTitle),
      content: i18n(toastTranslations.toastErrorContent),
    })

    logger.error(
      `[BUYBACK][API][ORDERS] Error getting ${tabName.value} orders`,
      {
        error: err as HttpApiError,
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    )
  } finally {
    isLoadingOrders.value = false
  }
}

async function handleFiltering(newFilters: OrderFilters) {
  filters.value = newFilters
  await fetchOrders()
}

async function fetchNextOrders() {
  isLoadingMoreOrders.value = true
  await fetchOrders(true)
  isLoadingMoreOrders.value = false
}
</script>
